import { useEffect, useRef, useState } from "react";

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

export const speechSupported = !!synth && typeof window.SpeechSynthesisUtterance !== "undefined";

export function toSpeakable(md) {
    return (md || "")
        .split("```action")[0]
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/[*_`#>~|]/g, "")
        .replace(/^\s*[-•]\s+/gm, "")
        .replace(/⚠️/g, "Uyarı:")
        .replace(/\s+/g, " ")
        .trim();
}

function chunk(text, max = 180) {
    const parts = text.match(/[^.!?;:]+[.!?;:]*/g) || [text];
    const out = [];
    let cur = "";
    for (const p of parts) {
        if ((cur + p).length > max && cur) {
            out.push(cur.trim());
            cur = "";
        }
        cur += p;
    }
    if (cur.trim()) out.push(cur.trim());
    return out;
}

const pickVoice = () =>
    (synth?.getVoices() || []).find((v) => v.lang?.toLowerCase().startsWith("tr")) || null;

export function useSpeech() {
    const [speakingId, setSpeakingId] = useState(null);
    const [hasTrVoice, setHasTrVoice] = useState(true);
    const voiceRef = useRef(null);

    useEffect(() => {
        if (!speechSupported) return;
        const load = () => {
            voiceRef.current = pickVoice();
            if (synth.getVoices().length) setHasTrVoice(!!voiceRef.current);
        };
        load();
        synth.addEventListener?.("voiceschanged", load);
        return () => {
            synth.removeEventListener?.("voiceschanged", load);
            synth.cancel();
        };
    }, []);

    function stop() {
        if (!speechSupported) return;
        synth.cancel();
        setSpeakingId(null);
    }

    function speak(id, markdown) {
        if (!speechSupported) return;
        synth.cancel();
        const text = toSpeakable(markdown);
        if (!text) return;
        const pieces = chunk(text);
        setSpeakingId(id);
        pieces.forEach((p, i) => {
            const u = new SpeechSynthesisUtterance(p);
            u.lang = "tr-TR";
            if (voiceRef.current) u.voice = voiceRef.current;
            u.rate = 1.02;
            if (i === pieces.length - 1) {
                u.onend = () => setSpeakingId((cur) => (cur === id ? null : cur));
                u.onerror = u.onend;
            }
            synth.speak(u);
        });
    }

    return { speak, stop, speakingId, hasTrVoice, supported: speechSupported };
}
